import { FC, useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import { BarChartProps, ChartProps, LineChartProps } from './Chart.types';
import { isMobile } from '../../../../common/utils';
import { getChartColors } from '../../../../common/chartHooks';

const isBarChart = (props: ChartProps): props is BarChartProps => props.type === 'bar';
const isLineChart = (props: ChartProps): props is LineChartProps => props.type === 'line';

export const Chart: FC<ChartProps> = (props) => {
  const {
    type,
    title,
    subtitle = '',
    unit,
    stacked,
    trendline,
    horizontal,
    data,
    style = {},
    containerRef,
    trendlineLabel,
    disableAnimation = false,
  } = props;

  const chartRef = useRef<HTMLDivElement | null>(null);

  const baseConfig = {
    // Configuraciones de animación
    ...(disableAnimation && {
      animation: false, // Desactiva todas las animaciones
      animationDuration: 0, // Duración de animación en 0ms
      animationEasing: 'linear', // Tipo de animación más simple
      animationThreshold: 0, // Desactiva animaciones cuando hay muchos elementos
      progressive: 0, // Desactiva el renderizado progresivo
      progressiveThreshold: 0, // Umbral para renderizado progresivo
    }),
    title: {
      text: title || undefined,
      subtext: subtitle || undefined,
      left: 'center',
      top: '15',
      textStyle: {
        overflow: 'break',
        fontSize: 14,
        width: 300,
      },
      subtextStyle: {
        overflow: 'break',
        fontSize: 12,
        width: 300,
      },
    },
    toolbox: {
      feature: {
        saveAsImage: {
          title: '',
        },
      },
    },
    grid: {
      top: `${subtitle ? 125 : 100}`,
      left: 10,
      right: 20,
      containLabel: true,
    },
    legend: {
      left: 'center',
      bottom: 0,
      type: 'scroll',
    },
  };

  const createBoundaryLines = () => {
    if (isBarChart(props) || isLineChart(props)) {
      const { boundaries } = props;
      if (!boundaries) return [];

      return boundaries.map((boundary) => ({
        type: 'line',
        markLine: {
          silent: true,
          lineStyle: {
            color: boundary.level === 1 ? '#F79009' : '#F04438',
            type: 'solid',
            width: 2,
          },
          label: {
            show: false,
          },
          symbol: 'none',
          data: [{ yAxis: boundary.min }, { yAxis: boundary.max }],
        },
      }));
    }
    return [];
  };

  const calculateTrendline = (d: any[]) => {
    const xData = d.map((_, index) => index);
    const yData = d.map((item) => item.value[0].value);

    const xSum = xData.reduce((a, b) => a + b, 0);
    const ySum = yData.reduce((a, b) => a + b, 0);
    const xySum = xData.reduce((sum, x, i) => sum + x * yData[i], 0);
    const xSquaredSum = xData.reduce((sum, x) => sum + x * x, 0);

    const n = xData.length;

    const slope = (n * xySum - xSum * ySum) / (n * xSquaredSum - xSum * xSum);
    const intercept = (ySum - slope * xSum) / n;

    return xData.map((x) => Number((slope * x + intercept).toFixed(2)));
  };

  const addTrendlineOption = (d: any[]) => {
    return trendline
      ? [
          {
            name: trendlineLabel,
            type: 'line',
            smooth: true,
            showSymbol: false,
            data: calculateTrendline(d),
            lineStyle: {
              color: '#ff0000',
              type: 'dashed',
            },
          },
        ]
      : [];
  };

  const getPercentFormatter = (params: any) => `${params.marker}${params.name}: ${params.percent}%`;

  const getAxisFormatter = (params: any) =>
    params
      .map((item: any) => {
        return `${item.marker}${
          params.length > 1 ? item.seriesName : item.name
        }: ${item.value} ${unit || ''}`;
      })
      .join('<br>');

  const getTooltipPosition = (
    pos: number[],
    _: any,
    __: any,
    ___: any,
    size: { viewSize: number[] }
  ) => {
    if (pos[0] > size.viewSize[0] / 2) {
      return { right: size.viewSize[0] - pos[0], top: pos[1] };
    }
    return { left: pos[0], top: pos[1] };
  };

  const getPieOption = (chartData: any) => {
    const paletteColors = getChartColors(chartData.length);
    return {
      ...baseConfig,
      tooltip: {
        trigger: 'item',
        formatter: getPercentFormatter,
        position: getTooltipPosition,
      },
      color: paletteColors,
      series: [
        {
          type: 'pie',
          center: ['50%', '55%'],
          radius: ['40%', '70%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 6,
          },
          label: {
            show: false,
            position: 'center',
          },
          labelLine: {
            show: false,
          },
          data: chartData.map((item: any) => ({
            name: item.category,
            value: item.value[0].value,
          })),
        },
      ],
    };
  };

  const getBarOption = (chartData: any, chartUnit?: string | null) => {
    const boundaryLines = createBoundaryLines();
    const paletteColors = getChartColors(chartData[0].value.length);

    if (stacked) {
      const series = [
        ...chartData[0].value.map((val: any, i: number) => ({
          name: val.key,
          type: 'bar',
          stack: 'total',
          smooth: true,
          data: chartData.map((item: any) => {
            const value = item.value.find((v: any) => v.key === val.key)?.value || 0;
            return {
              value,
              itemStyle: {
                color: paletteColors[i],
                borderRadius: [0, 0, 0, 0],
              },
            };
          }),
          itemStyle: {
            color: paletteColors[i],
          },
        })),
        ...addTrendlineOption(chartData).map((o: any) => ({ ...o, stack: 'total' })),
        ...boundaryLines,
      ];

      // Aplicar redondeo solo al extremo final de la barra (lejos del eje)
      // borderRadius en ECharts: [top-left, top-right, bottom-right, bottom-left]
      // Los bordes pegados a los ejes (X en vertical, Y en horizontal) siempre son rectos
      const applyRoundedCorners = (s: any[], radius: number) => {
        if (s && s.length > 0) {
          s.forEach((item) => {
            if (item.itemStyle) {
              if (horizontal) {
                // Horizontal: redondear solo el extremo derecho (lejos del eje Y)
                item.itemStyle.borderRadius = [0, radius, radius, 0]; // top-right y bottom-right
              } else {
                // Vertical: redondear solo el extremo superior (lejos del eje X)
                item.itemStyle.borderRadius = [radius, radius, 0, 0]; // top-left y top-right
              }
            }
          });
        }
      };

      // Índice de la última serie de barras (sin contar trendlines ni boundaryLines)
      const lastBarSeriesIndex = chartData[0].value.length - 1;

      // Aplicar redondeo solo a la serie final (arriba en vertical, derecha en horizontal)
      applyRoundedCorners(series[lastBarSeriesIndex].data, 20);

      return {
        ...baseConfig,
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
          },
          formatter: getAxisFormatter,
          position: getTooltipPosition,
        },
        [horizontal ? 'xAxis' : 'yAxis']: {
          type: 'value',
          boundaryGap: [0, 0.01],
          axisLabel: {
            formatter: `{value} ${chartUnit || ''}`,
          },
        },
        [horizontal ? 'yAxis' : 'xAxis']: {
          type: 'category',
          data: chartData.map((item: any) => item.category),
        },
        series,
      };
    }

    return {
      ...baseConfig,
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        formatter: getAxisFormatter,
        position: getTooltipPosition,
      },
      [horizontal ? 'yAxis' : 'xAxis']: {
        type: 'category',
        data: chartData.map((item: any) => item.category),
      },
      [horizontal ? 'xAxis' : 'yAxis']: {
        type: 'value',
        boundaryGap: [0, 0.01],
        axisLabel: {
          formatter: `{value} ${chartUnit || ''}`,
        },
      },
      series: [
        ...chartData[0].value.map((val: any, i: number) => ({
          name: val.key || undefined,
          type: 'bar',
          data: chartData.map(
            (item: any) => item.value.find((v: any) => v.key === val.key)?.value || 0
          ),
          itemStyle: {
            color: paletteColors[i],
            borderRadius: horizontal ? [0, 20, 20, 0] : [20, 20, 0, 0],
          },
        })),
        ...addTrendlineOption(chartData),
        ...boundaryLines,
      ],
    };
  };

  const getLineOption = (chartData: any, chartUnit?: string | null) => {
    const paletteColors = getChartColors(chartData.length);
    const hasBoundaries =
      isLineChart(props) && props.boundaries !== undefined && props.boundaries.length >= 2;

    let coloredBoundaryLines;
    if (hasBoundaries && props.boundaries) {
      const [level1, level2] = props.boundaries;

      coloredBoundaryLines = {
        visualMap: {
          show: false,
          dimension: 1,
          pieces: [
            { lte: level2.min, color: '#F04438' },
            { gt: level2.min, lte: level1.min, color: '#F79009' },
            { gt: level1.min, lte: level1.max, color: '#667085' },
            { gt: level1.max, lte: level2.max, color: '#F79009' },
            { gt: level2.max, color: '#F04438' },
          ],
        },
        legend: {
          show: false,
        },
      };
    }

    const baseLineOption = {
      ...baseConfig,
      tooltip: {
        trigger: 'axis',
        formatter: getAxisFormatter,
        position: getTooltipPosition,
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: `{value} ${chartUnit || ''}`,
        },
      },
      xAxis: {
        type: 'category',
        data: chartData.map((item: any) => item.category),
      },
    };

    const boundaryLines = createBoundaryLines();

    if (stacked) {
      return {
        ...baseLineOption,
        series: [
          ...chartData[0].value.map((val: any, i: number) => ({
            name: val.key || undefined,
            type: 'line',
            stack: 'total',
            smooth: true,
            itemStyle: {
              color: paletteColors[i],
            },
            data: chartData.map(
              (item: any) => item.value.find((v: any) => v.key === val.key)?.value || 0
            ),
          })),
          ...addTrendlineOption(chartData),
          ...boundaryLines,
        ],
      };
    }

    return {
      ...baseLineOption,
      ...(hasBoundaries ? coloredBoundaryLines : {}),
      series: [
        ...chartData[0].value.map((val: any, i: number) => ({
          name: val.key || undefined,
          type: 'line',
          smooth: true,
          itemStyle: {
            color: paletteColors[i],
          },
          data: chartData.map(
            (item: any) => item.value.find((v: any) => v.key === val.key)?.value || 0
          ),
        })),
        ...addTrendlineOption(chartData),
        ...boundaryLines,
      ],
    };
  };

  const getAreaOption = (chartData: any, chartUnit?: string | null) => {
    const paletteColors = getChartColors(chartData[0].value.length);

    const baseAreaOption = {
      ...baseConfig,
      tooltip: {
        trigger: 'axis',
        formatter: getAxisFormatter,
        position: getTooltipPosition,
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: `{value} ${chartUnit || ''}`,
        },
      },
      xAxis: {
        type: 'category',
        data: chartData.map((item: any) => item.category),
      },
    };

    const series = chartData[0].value.map((val: any, i: number) => ({
      name: val.key || '',
      type: 'line',
      smooth: true,
      itemStyle: {
        color: paletteColors[i],
      },
      areaStyle: {},
      data: chartData.map(
        (item: any) => item.value.find((v: any) => v.key === val.key)?.value || 0
      ),
    }));

    if (stacked) {
      series.forEach((s: any) => {
        s.stack = 'total';
      });
    }

    return {
      ...baseAreaOption,
      series: [...series, ...addTrendlineOption(chartData)],
    };
  };

  const getSingleChartOption = (chartType: string, chartData: any, chartUnit?: string | null) => {
    switch (chartType) {
      case 'pie': {
        return getPieOption(chartData);
      }
      case 'bar': {
        const barOption = getBarOption(chartData, chartUnit);
        return barOption;
      }
      case 'line': {
        return getLineOption(chartData, chartUnit);
      }
      case 'area': {
        return getAreaOption(chartData, chartUnit);
      }
      default:
        return {};
    }
  };

  const getYAxisConfig = (chartUnit: string, chartData: any[]) => {
    const isPercentage = chartUnit === '%';
    const maxValue = Math.max(...chartData.flatMap((item) => item.value.map((v: any) => v.value)));

    return {
      type: 'value',
      axisLabel: {
        formatter: `{value} ${chartUnit}`,
      },
      max: isPercentage ? Math.max(100, maxValue) : undefined,
    };
  };

  const getMultiChartOption = (multiData: any[]) => {
    if (multiData.length !== 2) {
      console.error('Multiple chart type must have exactly two charts');
      return {};
    }

    const [leftChart, rightChart] = multiData;
    const leftOption: any = getSingleChartOption(leftChart.type, leftChart.data, leftChart.unit);
    const rightOption: any = getSingleChartOption(
      rightChart.type,
      rightChart.data,
      rightChart.unit
    );

    const allCategories = [
      ...new Set([
        ...leftChart.data.map((item: any) => item.category),
        ...rightChart.data.map((item: any) => item.category),
      ]),
    ];

    return {
      ...baseConfig,
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
        },
      },
      legend: {
        ...baseConfig.legend,
        data: [
          ...leftOption.series.map((s: any) => s.name),
          ...rightOption.series.map((s: any) => s.name),
        ],
      },
      xAxis: {
        type: 'category',
        data: allCategories,
      },
      yAxis: [
        getYAxisConfig(leftChart.unit, leftChart.data),
        {
          ...getYAxisConfig(rightChart.unit, rightChart.data),
          splitLine: {
            show: false,
          },
        },
      ],
      series: [
        ...leftOption.series.map((s: any) => ({ ...s, yAxisIndex: 0 })),
        ...rightOption.series.map((s: any) => ({
          ...s,
          yAxisIndex: 1,
          lineStyle: { color: '#cc0000' },
        })),
      ],
    };
  };

  const getOption = () => {
    if (type === 'multiple') {
      return getMultiChartOption(data);
    } else {
      const singleChartOption = getSingleChartOption(type, data, unit);
      return singleChartOption;
    }
  };

  useEffect(() => {
    if (chartRef.current) {
      const chart = echarts.init(chartRef.current);
      const option = getOption();

      const containerWidth = chartRef.current.offsetWidth;
      const textWidth = Math.max(200, containerWidth - 60);
      if (option.title) {
        option.title.textStyle = { ...option.title.textStyle, width: textWidth };
        if (option.title.subtextStyle) {
          option.title.subtextStyle = { ...option.title.subtextStyle, width: textWidth };
        }
      }

      chart.setOption(option);

      const resizeObserver = new ResizeObserver(() => {
        chart.resize();
      });

      containerRef?.current && resizeObserver.observe(containerRef.current);

      return () => {
        chart.dispose();
        containerRef?.current && resizeObserver.unobserve(containerRef.current);
      };
    }
  }, [chartRef, containerRef?.current?.offsetHeight]);

  return (
    <div style={{ height: 450 }}>
      <div
        ref={chartRef}
        style={{ width: '100%', height: '100%', ...(style as React.CSSProperties) }}
      />
    </div>
  );
};
